/**
 * URL search-param helpers — shared across every workspace search surface.
 *
 * The 6 workspace tabs (Conversazioni, Documenti, Finanza, Calendario,
 * Lavoro, Tecnico) all encode their filter chip state into the URL via
 * TanStack Router's ``validateSearch`` so deep links round-trip the full
 * search context (Linear / Notion pattern). These helpers keep the
 * encode + decode contract DRY:
 *
 *   - ``parseCSV`` — coerce a single ``?provider=email,slack`` string
 *     value back into a ``readonly string[]`` after URL-decode.
 *   - ``buildSearchParams`` — pack a typed filter record into
 *     ``URLSearchParams`` (omits null / undefined / empty arrays).
 *
 * Why a string-CSV instead of repeated ``?provider=email&provider=slack``:
 * URLSearchParams' repeated-key behaviour is fine on send but TanStack
 * Router's loosely-typed ``Record<string, unknown>`` decode collapses
 * repeats to ``string | string[]`` non-deterministically — a single CSV
 * string is the canonical "one key, one value" contract every route
 * already follows (mirror of ``listInbox`` in ``endpoints/inbox.ts``).
 *
 * NOTE: callers responsible for typing the OUTPUT of ``parseCSV`` (e.g.
 * narrowing ``string[]`` to a ``InboxProviderId[]`` literal union) —
 * we don't validate against a vocabulary here so the helper stays
 * vocabulary-free across tabs.
 */

/**
 * Parse a CSV string (or unknown URL-search value) into a readonly
 * string[]. Empty / non-string inputs return an empty array — never
 * ``null`` / ``undefined`` so consumer ``useMemo`` deps stay stable.
 *
 * Handles four input shapes:
 *   - ``"a,b,c"``       → ``["a","b","c"]``
 *   - ``""``             → ``[]``
 *   - ``["a","b,c"]``   → ``["a","b","c"]``  (ARRAY: TanStack re-validates
 *       an already-parsed search value as ``string[]``, and repeated query
 *       keys decode to an array — without this branch a round-trip through
 *       ``validateSearch`` would silently drop array values to ``[]``, which
 *       broke document-group collapse + chip filters not persisting,
 *       June 2026)
 *   - other non-string  → ``[]`` (defensive)
 *
 * Whitespace around segments is trimmed; empty segments dropped (so
 * ``"a, ,c"`` → ``["a", "c"]`` — useful for hand-edited URLs).
 */
export function parseCSV(value: unknown): readonly string[] {
  // ARRAY: a previously-validated search value (or repeated query keys) comes
  // back as ``string[]`` — flatten each element through parseCSV so both
  // ``["a","b"]`` and ``["a,b"]`` normalise correctly. Without this the
  // re-validation round-trip dropped array values to ``[]``.
  if (Array.isArray(value)) {
    return value.flatMap((v) => parseCSV(v));
  }
  if (typeof value !== "string" || value.length === 0) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Build a ``URLSearchParams`` instance from a typed filter record,
 * applying the DRY encode rules every workspace search endpoint
 * follows:
 *
 *   - ``undefined`` / ``null``     → key omitted
 *   - ``""`` (empty string)         → key omitted
 *   - ``readonly[]`` (length 0)     → key omitted
 *   - ``readonly[]`` (length >= 1)  → joined with ``","`` (CSV)
 *   - ``boolean``                    → ``"true"`` / ``"false"``
 *   - ``number``                     → ``String(n)`` (``NaN`` omitted)
 *   - ``string`` (non-empty)         → as-is
 *
 * Object / Date / nested-array values are NOT supported — workspace
 * filters never need them and the helper would have to opinionate on
 * an encoding. If a future tab needs e.g. JSON-encoded ranges, add a
 * dedicated case here so every endpoint gets the same handling.
 *
 * @example
 *   const qs = buildSearchParams({
 *     q: "fattura",
 *     provider: ["email", "slack"],
 *     unread: true,
 *     limit: 50,
 *   });
 *   qs.toString() === "q=fattura&provider=email%2Cslack&unread=true&limit=50"
 */
export function buildSearchParams<T extends object>(params: Readonly<T>): URLSearchParams {
  const out = new URLSearchParams();
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null) continue;

    if (Array.isArray(raw)) {
      if (raw.length === 0) continue;
      // CSV — never escape commas client-side, URLSearchParams handles
      // percent-encoding on toString().
      out.set(key, raw.map((v) => String(v)).join(","));
      continue;
    }

    if (typeof raw === "string") {
      if (raw.length === 0) continue;
      out.set(key, raw);
      continue;
    }

    if (typeof raw === "boolean") {
      out.set(key, raw ? "true" : "false");
      continue;
    }

    if (typeof raw === "number") {
      if (!Number.isFinite(raw)) continue;
      out.set(key, String(raw));
      continue;
    }

    // Defensive: unknown shape — coerce to string only if it has a
    // meaningful toString (skip plain objects which would serialize
    // to ``[object Object]`` and silently corrupt the URL).
    const proto = Object.getPrototypeOf(raw);
    if (proto === null || proto === Object.prototype) continue;
    const stringified = String(raw);
    if (stringified.length === 0 || stringified === "[object Object]") continue;
    out.set(key, stringified);
  }
  return out;
}

/**
 * Convenience wrapper — builds the params then formats the final
 * ``"?…"`` query string (empty when no params resolve). Matches the
 * pattern in every endpoint file: build → if non-empty, append.
 */
export function buildQueryString<T extends object>(params: Readonly<T>): string {
  const qs = buildSearchParams(params).toString();
  return qs.length === 0 ? "" : `?${qs}`;
}
